import LockRoundedIcon from '@mui/icons-material/LockRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { ApiError, getHealth, login, verifyMfa } from './api';
import { BrandLockup } from './BrandLockup';
import { SupportFooter } from './SupportFooter';

interface LoginFields {
  email: string;
  password: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [challengeToken, setChallengeToken] = useState<string>();
  const [mfaCode, setMfaCode] = useState('');
  const { register, handleSubmit, formState } = useForm<LoginFields>();
  const health = useQuery({ queryKey: ['health'], queryFn: () => getHealth(), retry: 1 });
  const finish = async (role: 'PARTNER' | 'COLLABORATOR' | 'CLIENT') => {
    await queryClient.invalidateQueries({ queryKey: ['me'] });
    const from = (location.state as { from?: string } | null)?.from;
    const defaultDestination =
      role === 'PARTNER' ? '/crm/companies' : role === 'CLIENT' ? '/portal' : '/crm/projects';
    void navigate(from?.startsWith('/crm') ? from : defaultDestination, { replace: true });
  };
  const loginMutation = useMutation({
    mutationFn: ({ email, password }: LoginFields) => login(email, password),
    onSuccess: async (result) => {
      if (result.mfaRequired) setChallengeToken(result.challengeToken);
      else await finish(result.user.role);
    },
  });
  const mfaMutation = useMutation({
    mutationFn: () => verifyMfa(challengeToken ?? '', mfaCode),
    onSuccess: async (result) => finish(result.user.role),
  });
  const error = loginMutation.error ?? mfaMutation.error;

  return (
    <Container component="main" maxWidth="sm" sx={{ py: { xs: 5, md: 10 } }}>
      <Stack spacing={4}>
        <Box>
          <Box
            sx={{
              bgcolor: 'primary.dark',
              borderRadius: 2.5,
              display: 'inline-flex',
              px: 2,
              py: 1.5,
            }}
          >
            <BrandLockup />
          </Box>
          <Typography
            component="h1"
            sx={{ fontSize: { xs: '2.5rem', sm: '3rem' }, mt: 1 }}
            variant="h2"
          >
            Accedi a Go-Live
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Accedi con l’account nominativo ricevuto tramite invito.
          </Typography>
        </Box>
        <Card>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {!challengeToken ? (
              <Box
                component="form"
                onSubmit={(event) =>
                  void handleSubmit((values) => loginMutation.mutate(values))(event)
                }
              >
                <Stack spacing={3}>
                  <TextField
                    autoComplete="email"
                    autoFocus
                    error={Boolean(formState.errors.email)}
                    fullWidth
                    label="Email"
                    type="email"
                    {...register('email', { required: true })}
                  />
                  <TextField
                    autoComplete="current-password"
                    error={Boolean(formState.errors.password)}
                    fullWidth
                    label="Password"
                    type="password"
                    {...register('password', { required: true, minLength: 12 })}
                  />
                  <Button
                    disabled={loginMutation.isPending}
                    size="large"
                    startIcon={
                      loginMutation.isPending ? <CircularProgress size={18} /> : <LockRoundedIcon />
                    }
                    type="submit"
                    variant="contained"
                  >
                    Accedi
                  </Button>
                  <Button component="a" href="/forgot-password" size="small">
                    Password dimenticata?
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Stack spacing={3}>
                <Typography sx={{ fontWeight: 800 }} variant="h6">
                  Verifica in due passaggi
                </Typography>
                <Typography color="text.secondary">
                  Inserisci il codice TOTP oppure uno dei codici di recupero.
                </Typography>
                <TextField
                  autoComplete="one-time-code"
                  autoFocus
                  label="Codice MFA"
                  onChange={(event) => setMfaCode(event.target.value)}
                  value={mfaCode}
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                />
                <Button
                  disabled={mfaMutation.isPending || mfaCode.length < 6}
                  onClick={() => mfaMutation.mutate()}
                  variant="contained"
                >
                  Verifica
                </Button>
              </Stack>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error instanceof ApiError ? error.message : 'Accesso non riuscito'}
              </Alert>
            )}
          </CardContent>
        </Card>
        <Chip
          color={health.isSuccess ? 'success' : 'default'}
          label={health.isSuccess ? 'Piattaforma operativa' : 'Verifica piattaforma…'}
          sx={{ alignSelf: 'flex-start' }}
          variant="outlined"
        />
        <SupportFooter />
      </Stack>
    </Container>
  );
}
